import * as React from "react";
import { format } from "date-fns";
import { DateRange, DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import {
  Box,
  Button,
  InputAdornment,
  Paper,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";

export type RangeValue = {
  from?: Date;
  to?: Date;
};

type PresetKey =
  | "today"
  | "last7"
  | "last30"
  | "thisMonth"
  | "clear";

type DateRangeSelectorProps = {
  value: RangeValue;
  onChange: (value: RangeValue) => void;
  onApply?: (value: RangeValue) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  numberOfMonths?: number;
  minDate?: Date;
  maxDate?: Date;
};


const formatRangeLabel = (range: RangeValue, placeholder: string) => {
  if (!range.from && !range.to) return placeholder;
  if (range.from && !range.to) return `${format(range.from, "yyyy-MM-dd")} -`;
  if (!range.from && range.to) return `- ${format(range.to, "yyyy-MM-dd")}`;
  return `${format(range.from!, "yyyy-MM-dd")} - ${format(range.to!, "yyyy-MM-dd")}`;
};

export default function DateRangeSelector({
  value,
  onChange,
  onApply,
  label = "Date range",
  placeholder = "Select date range",
  disabled = false,
  className = "",
  numberOfMonths = 1,
  minDate,
  maxDate,
}: DateRangeSelectorProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [draft, setDraft] = React.useState<DateRange | undefined>({
    from: value.from,
    to: value.to,
  });

  React.useEffect(() => {
    setDraft({
      from: value.from,
      to: value.to,
    });
  }, [value.from?.getTime(), value.to?.getTime()]);

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setDraft({
      from: value.from,
      to: value.to,
    });
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    const nextValue: RangeValue = {
      from: draft?.from,
      to: draft?.to,
    };
    onChange(nextValue);
    onApply?.(nextValue);
    handleClose();
  };

  const handleCancel = () => {
    setDraft({
      from: value.from,
      to: value.to,
    });
    handleClose();
  };

  const handleClear = () => {
    const emptyValue = {};
    setDraft(undefined);
    onChange(emptyValue);
    onApply?.(emptyValue);
    handleClose();
  };

  return (
    <div className={className}>
      <Box sx={{ width: { xs: "100%", md: "50%" }, pt: 2 }}>
        <TextField
          fullWidth
          size="small"
          label={label}
          color="success"
          value={formatRangeLabel(value, placeholder)}
          onClick={handleOpen}
          disabled={disabled}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <CalendarMonth color="success" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 3,
              overflow: "hidden",
            },
          },
        }}
      >
        <Paper className="w-full min-w-[300px] max-w-[900px] p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <Typography variant="subtitle1" fontWeight={600}>
                Select date range
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatRangeLabel(
                  { from: draft?.from, to: draft?.to },
                  "No range selected"
                )}
              </Typography>
            </div>
            <Box
              sx={{
                "& .rdp-root": {
                  "--rdp-accent-color": "#33ae33",
                  "--rdp-range_middle-background-color": "rgba(22, 163, 74, 0.18)",
                  "--rdp-range_middle-color": "inherit",
                },
                "& .rdp-day_button": {
                  borderRadius: "10px",
                },
                "& .rdp-day_selected .rdp-day_button, & .rdp-day_button.rdp-day_selected": {
                  backgroundColor: "#33ae33 !important",
                  color: "#fff !important",
                },
                "& .rdp-day_range_start .rdp-day_button": {
                  backgroundColor: "#33ae33 !important",
                  color: "#fff !important",
                },
                "& .rdp-day_range_end .rdp-day_button": {
                  backgroundColor: "#33ae33 !important",
                  color: "#fff !important",
                },
                "& .rdp-day_range_middle": {
                  backgroundColor: "rgba(22, 163, 74, 0.18) !important",
                  color: "inherit",
                },
              }}
            >
              <DayPicker
                mode="range"
                selected={draft}
                onSelect={setDraft}
                numberOfMonths={numberOfMonths}
                pagedNavigation
                showOutsideDays
                disabled={[
                  ...(minDate ? [{ before: minDate }] : []),
                  ...(maxDate ? [{ after: maxDate }] : []),
                ]}
              />
            </Box>

            <div className="flex items-center justify-between">
              <Button color="inherit" onClick={handleClear}>
                Clear
              </Button>

              <div className="flex gap-2">
                <Button onClick={handleCancel}>Cancel</Button>
                <Button
                  variant="outlined"
                  onClick={handleApply}
                  disabled={!draft?.from || !draft?.to}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </Paper>
      </Popover>
    </div>
  );
}